import { create } from 'zustand';

import type { FeedbackCategory } from '@/services/feedback.service';

/**
 * The Send feedback modal, openable from anywhere (YAR-482).
 *
 * Two ways in — the nav drawer's feedback card and Settings › Help & About —
 * open ONE modal, mounted once in AppShell. Both call `openFeedback()`.
 *
 * The draft lives here too (YAR-483): if the parent closes the modal by
 * accident (Cancel, Esc, backdrop) the text and category come back the next
 * time it opens, from either entry point. It is cleared on a successful send.
 * Deliberately memory only — no localStorage: feedback text has no business
 * sitting on disk, and a reload is not an "accidental close".
 */
interface FeedbackStoreState {
  isOpen: boolean;
  /**
   * Where focus goes when the modal closes. Captured at open time so the
   * drawer entry can hand over the page element that opened the drawer —
   * the card itself sits inside the closed drawer and must not get focus
   * back (YAR-484 AC 9).
   */
  returnFocusTo: HTMLElement | null;
  category: FeedbackCategory | null;
  message: string;
}

interface FeedbackStoreActions {
  openFeedback: (options?: { returnFocusTo?: HTMLElement | null }) => void;
  closeFeedback: () => void;
  setCategory: (category: FeedbackCategory | null) => void;
  setMessage: (message: string) => void;
  clearDraft: () => void;
}

function currentlyFocused(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  const el = document.activeElement;
  return el instanceof HTMLElement && el !== document.body ? el : null;
}

export const useFeedbackStore = create<
  FeedbackStoreState & FeedbackStoreActions
>()((set) => ({
  isOpen: false,
  returnFocusTo: null,
  category: null,
  message: '',
  openFeedback: (options) =>
    set({
      isOpen: true,
      returnFocusTo:
        options && 'returnFocusTo' in options
          ? (options.returnFocusTo ?? null)
          : currentlyFocused(),
    }),
  closeFeedback: () => set({ isOpen: false }),
  setCategory: (category) => set({ category }),
  setMessage: (message) => set({ message }),
  clearDraft: () => set({ category: null, message: '' }),
}));
